import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import axios from "axios";
// Import components
import AccessPage from "../Shared/accessPage";
import Pagination from "../Pagination/Pagination";
import NoData from "../common/noDataComponetnt";
import { API_URL } from "../../utils/constant";

const BlackList = () => {
    const { orgId } = useParams();
    const myRef = useRef(null);

    // Get organization ID
    const id = useSelector(state => {
        if (orgId) {
            return orgId;
        } else {
            return state?.summary?.localClient?._id || state?.summary?.clients?.[0]?._id;
        }
    });

    // Get user permissions and auth info
    const userPermission = useSelector(state => {
        return state?.auth?.user?.modulePermissionsConfig?.bs?.e;
    });
    const auth = useSelector((state) => state?.auth?.user);

    // Get client info
    const client = useSelector(
        (state) => state?.summary?.localClient || state?.summary?.clients?.[0]
    );

    // State variables
    const [blacklistData, setBlacklistData] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 10,
        hasNextPage: false,
        hasPrevPage: false
    });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [search, setSearch] = useState("");
    const [platform, setPlatform] = useState("all");

    // Modal states
    const [isCreateModal, setCreateModal] = useState(false);
    const [isEditModal, setEditModal] = useState(false);
    const [isDeleteModal, setDeleteModal] = useState(false);
    const [isBulkDeleteModal, setBulkDeleteModal] = useState(false);
    const [isBulkUpdateModal, setBulkUpdateModal] = useState(false);

    // Form data
    const [formData, setFormData] = useState({
        platform: "",
        userName: "",
        blackListedKeywords: [],
        description: ""
    });
    const [editingId, setEditingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    // Keyword input
    const [keywordInput, setKeywordInput] = useState("");
    const [bulkKeywordInput, setBulkKeywordInput] = useState("");

    // Selection states
    const [selectedIds, setSelectedIds] = useState([]);
    const [multiCheckTrue, setMultiCheckTrue] = useState(false);
    
    // Keywords display state
    const [expandedKeywords, setExpandedKeywords] = useState({});

    // Scan states
    const [scanningIds, setScanningIds] = useState(new Set());

    // Vendor info
    const vendorName = sessionStorage.getItem('vendorName');
    const vendorId = sessionStorage.getItem('vendorId');

    // Available platforms
    const platforms = [
        { value: "facebook", label: "Facebook" },
        { value: "instagram", label: "Instagram" },
        { value: "twitter", label: "Twitter" },
        { value: "tiktok", label: "TikTok" }
    ];

    // API Functions
    const fetchBlacklistEntries = async () => {
        if (!id) return;

        setLoading(true);
        try {
            let url = `${API_URL}/organisations/${id}/social-media-blacklist?page=${page}&limit=${pageSize}`;

            if (platform && platform !== 'all') {
                url += `&platform=${platform}`;
            }

            if (search) {
                url += `&search=${search}`;
            }

            const response = await axios.get(url);

            if (response.data.success) {
                setBlacklistData(response.data.data.entries || []);
                setPagination(response.data.data.pagination || {});
            } else {
                toastr.error("Error", "Failed to fetch blacklist entries");
            }
        } catch (error) {
            console.error("Error fetching blacklist entries:", error);
            toastr.error("Error", "Failed to fetch blacklist entries");
        } finally {
            setLoading(false);
        }
    };

    const createBlacklistEntry = async () => {
        if (!id || !formData.platform || !formData.userName || formData.blackListedKeywords.length === 0) {
            toastr.error("Error", "Please fill all required fields and add at least one keyword");
            return;
        }

        try {
            const response = await axios.post(`${API_URL}/organisations/${id}/social-media-blacklist`, formData);

            if (response.data.success) {
                toastr.success("Success", response.data.message || "Blacklist entry created successfully");
                setCreateModal(false);
                resetForm();
                fetchBlacklistEntries();
            } else {
                toastr.error("Error", response.data.message || "Failed to create blacklist entry");
            }
        } catch (error) {
            console.error("Error creating blacklist entry:", error);
            toastr.error("Error", "Failed to create blacklist entry");
        }
    };

    const updateBlacklistEntry = async () => {
        if (!id || !editingId || !formData.platform || !formData.userName || formData.blackListedKeywords.length === 0) {
            toastr.error("Error", "Please fill all required fields and add at least one keyword");
            return;
        }

        try {
            const response = await axios.put(`${API_URL}/organisations/${id}/social-media-blacklist/${editingId}`, formData);

            if (response.data.success) {
                toastr.success("Success", response.data.message || "Blacklist entry updated successfully");
                setEditModal(false);
                resetForm();
                fetchBlacklistEntries();
            } else {
                toastr.error("Error", response.data.message || "Failed to update blacklist entry");
            }
        } catch (error) {
            console.error("Error updating blacklist entry:", error);
            toastr.error("Error", "Failed to update blacklist entry");
        }
    };

    const deleteBlacklistEntry = async () => {
        if (!id || !deletingId) return;

        try {
            const response = await axios.delete(`${API_URL}/organisations/${id}/social-media-blacklist/${deletingId}`);

            if (response.data.success) {
                toastr.success("Success", response.data.message || "Blacklist entry deleted successfully");
                setDeleteModal(false);
                setDeletingId(null);
                fetchBlacklistEntries();
            } else {
                toastr.error("Error", response.data.message || "Failed to delete blacklist entry");
            }
        } catch (error) {
            console.error("Error deleting blacklist entry:", error);
            toastr.error("Error", "Failed to delete blacklist entry");
        }
    };

    const bulkDeleteBlacklistEntries = async () => {
        if (!id || selectedIds.length === 0) {
            toastr.error("Error", "Please select entries to delete");
            return;
        }

        try {
            const response = await axios.post(`${API_URL}/organisations/${id}/social-media-blacklist/bulk-delete`, {
                entryIds: selectedIds
            });

            if (response.data.success) {
                toastr.success("Success", response.data.message || `Successfully deleted ${selectedIds.length} blacklist entries`);
                setBulkDeleteModal(false);
                setSelectedIds([]);
                setMultiCheckTrue(false);
                fetchBlacklistEntries();
            } else {
                toastr.error("Error", response.data.message || "Failed to delete blacklist entries");
            }
        } catch (error) {
            console.error("Error bulk deleting blacklist entries:", error);
            toastr.error("Error", "Failed to delete blacklist entries");
        }
    };
    
    const bulkUpdateKeywords = async () => {
        if (!id || selectedIds.length === 0) {
            toastr.error("Error", "Please select entries to update");
            return;
        }
        
        // Parse keywords from the input, removing duplicates and empty strings
        const keywords = [...new Set(bulkKeywordInput.split(',')
            .map(keyword => keyword.trim())
            .filter(keyword => keyword !== '')
        )];
        
        if (keywords.length === 0) {
            toastr.error("Error", "Please enter at least one keyword");
            return;
        }

        try {
            const response = await axios.post(`${API_URL}/organisations/${id}/social-media-blacklist/bulk-update-keywords`, {
                ids: selectedIds,
                blackListedKeywords: keywords
            });

            if (response.data.success) {
                toastr.success("Success", response.data.message || `Successfully updated keywords for ${selectedIds.length} blacklist entries`);
                setBulkUpdateModal(false);
                setSelectedIds([]);
                setMultiCheckTrue(false);
                setBulkKeywordInput("");
                fetchBlacklistEntries();
            } else {
                toastr.error("Error", response.data.message || "Failed to update keywords");
            }
        } catch (error) {
            console.error("Error updating keywords:", error);
            toastr.error("Error", "Failed to update keywords");
        }
    };

    const triggerManualScan = async (entry) => {
        if (!id || !entry.userName || !entry.platform) {
            toastr.error("Error", "Invalid entry data for scan");
            return;
        }

        // Add to scanning set immediately
        setScanningIds(prev => new Set(prev).add(entry._id));
        
        try {
            const response = await axios.post(`${API_URL}/manual-social-media-scan`, {
                organisationId: id,
                userName: entry.userName,
                platform: entry.platform
            });

            if (response.data.success) {
                toastr.success("Success", `Scan completed for ${entry.userName} on ${entry.platform}`);
                // Optionally refresh the blacklist data
                setTimeout(() => {
                    fetchBlacklistEntries();
                }, 1000);
            } else {
                toastr.error("Error", response.data.message || "Failed to trigger manual scan");
            }
        } catch (error) {
            console.error("Error triggering manual scan:", error);
            toastr.error("Error", "Failed to trigger manual scan");
        } finally {
            // Remove from scanning set after a delay to show the completed state
            setTimeout(() => {
                setScanningIds(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(entry._id);
                    return newSet;
                });
            }, 2000); // Show scanning state for 2 seconds
        }
    };

    // Effects
    useEffect(() => {
        if (id) {
            fetchBlacklistEntries();
        }
    }, [id, page, pageSize, platform, search]);

    // Update multiCheckTrue state based on selected items
    useEffect(() => {
        if (blacklistData.length > 0) {
            setMultiCheckTrue(selectedIds.length === blacklistData.length && selectedIds.length > 0);
        }
    }, [selectedIds, blacklistData]);

    // Reset expanded keywords when data changes
    useEffect(() => {
        setExpandedKeywords({});
    }, [blacklistData]);

    // Form handlers
    const resetForm = () => {
        setFormData({
            platform: "",
            userName: "",
            blackListedKeywords: [],
            description: ""
        });
        setKeywordInput("");
        setEditingId(null);
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const addKeyword = () => {
        if (keywordInput.trim() && !formData.blackListedKeywords.includes(keywordInput.trim())) {
            setFormData(prev => ({
                ...prev,
                blackListedKeywords: [...prev.blackListedKeywords, keywordInput.trim()]
            }));
            setKeywordInput("");
        }
    };

    const removeKeyword = (keyword) => {
        setFormData(prev => ({
            ...prev,
            blackListedKeywords: prev.blackListedKeywords.filter(k => k !== keyword)
        }));
    };

    const handleKeywordInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addKeyword();
        }
    };

    // CRUD operation handlers
    const handleEdit = (entry) => {
        setFormData({
            platform: entry.platform,
            userName: entry.userName,
            blackListedKeywords: [...entry.blackListedKeywords],
            description: entry.description || ""
        });
        setEditingId(entry._id);
        setEditModal(true);
    };

    const handleDeleteConfirm = (entryId) => {
        setDeletingId(entryId);
        setDeleteModal(true);
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) {
            toastr.error("Error", "Please select entries to delete");
            return;
        }
        setBulkDeleteModal(true);
    };
    
    const handleBulkUpdateKeywords = () => {
        if (selectedIds.length === 0) {
            toastr.error("Error", "Please select entries to update");
            return;
        }
        setBulkUpdateModal(true);
    };

    // Selection handlers
    const multicheck = (data) => {
        const allIds = data?.map((item) => item._id);
        if (selectedIds.length === 0 || selectedIds.length < allIds.length) {
            setMultiCheckTrue(true);
            setSelectedIds(allIds);
        } else {
            setSelectedIds([]);
            setMultiCheckTrue(false);
        }
    };

    const singleClick = (itemId) => {
        setSelectedIds((prevSelectedIds) => {
            if (prevSelectedIds.includes(itemId)) {
                return prevSelectedIds.filter(id => id !== itemId);
            } else {
                return [...prevSelectedIds, itemId];
            }
        });
    };

    const toggleKeywordsDisplay = (entryId) => {
        setExpandedKeywords(prev => ({
            ...prev,
            [entryId]: !prev[entryId]
        }));
    };

    let clientName = "";
    if (JSON.parse(localStorage.getItem("OrgName"))) {
        if (sessionStorage.getItem('vendorId')) {
            clientName = sessionStorage.getItem('vendorName');
        } else {
            clientName = JSON.parse(localStorage.getItem("OrgName"));
        }
    }

    return (
        <>
            <div className="container-fluid">
                <AccessPage />
                <div className="block-header">
                    <div className="row clearfix">
                        <div className="col-md-6 col-sm-12">
                            <h1 className="welcome-text">Social Media Blacklist</h1>
                            <nav aria-label="breadcrumb" className="mt-1">
                                <ol className="breadcrumb">
                                    <li className="breadcrumb-item">
                                        {vendorId ? (
                                            <Link to="/vendor" onClick={() => sessionStorage.clear()}>
                                                Vendor Assessment
                                            </Link>
                                        ) : (
                                            <Link to="/" className="welcome-textSmall">Home</Link>
                                        )}
                                    </li>
                                    {vendorName && (
                                        <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                                            <Link to={`/dashboard/vendor/${vendorId}`}>{vendorName}</Link>
                                        </li>
                                    )}
                                    <li className="breadcrumb-item active" aria-current="page" style={{ color: "white" }}>
                                        Social Media Blacklist
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="col-md-6 col-sm-12 text-right hidden-xs d-flex justify-content-end">
                            {userPermission && (
                                <button
                                    onClick={() => setCreateModal(true)}
                                    className="btn btn-primary mr-1 height40"
                                >
                                    Add New Entry
                                </button>
                            )}
                            {selectedIds.length > 0 && userPermission && (
                                <>
                                    <button
                                        onClick={handleBulkUpdateKeywords}
                                        className="btn btn-info mr-1 height40"
                                    >
                                        Update Keywords ({selectedIds.length})
                                    </button>
                                    <button
                                        onClick={handleBulkDelete}
                                        className="btn btn-danger mr-1 height40"
                                    >
                                        Delete Selected ({selectedIds.length})
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* Info Alert Banner */}
                <div className="card border-info mb-3">
                    <div className="card-body">
                        <div className="d-flex align-items-start">
                            <div className="mr-3">
                                <i className="fa fa-shield text-info" style={{ fontSize: "24px" }}></i>
                            </div>
                            <div className="flex-grow-1">
                                <h6 className="text-info mb-2">
                                    🛡️ Social Media Blacklist Management
                                </h6>
                                <p className="mb-2">
                                    Monitor and manage blacklisted keywords and users across social media platforms to enhance brand protection and threat detection.
                                </p>
                                <div className="mb-3">
                                    <strong>📋 Features:</strong>
                                    <ul className="mb-0 mt-2" style={{ paddingLeft: "20px" }}>
                                        <li>Add platform-specific blacklisted users and keywords for monitoring</li>
                                        <li>Track suspicious activities and potential brand threats</li>
                                        <li>Manage comprehensive keyword lists for automated detection</li>
                                        <li>Bulk operations for efficient management</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card card_tableBox">
                    <div className="body">
                        <div className="row mb-3 clearfix">
                            <div className="col-lg-12 col-md-12">
                                <div className="row clearfix">
                                    <div className="col-lg-4 col-md-12">
                                        <select
                                            className="custom-select custom-select-sm form-control form-control-sm"
                                            value={platform}
                                            onChange={(e) => { setPlatform(e.target.value); setPage(1); }}
                                        >
                                            <option value="all">All Platforms</option>
                                            {platforms.map(p => (
                                                <option key={p.value} value={p.value}>{p.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-lg-4 col-md-12">
                                        <div className="search-input-container" style={{ position: "relative", width: "100%" }}>
                                            <input
                                                type="text"
                                                className="form-control form-control-sm"
                                                placeholder="Search users or keywords..."
                                                value={search}
                                                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                                            />
                                            <span className="search-icon" style={{ position: "absolute", right: "10px", top: "50%", transform: "translateY(-50%)", color: "#495057" }}>
                                                <i className="fa fa-search" aria-hidden="true"></i>
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <div className="table-responsive mt-3" ref={myRef}>
                                    <table className="table table-custom spacing5 mb-0">
                                        <thead>
                                            <tr>
                                                {userPermission && (
                                                    <th>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedIds.length > 0 && multiCheckTrue}
                                                            onChange={() => multicheck(blacklistData)}
                                                            className="checkUncheck"
                                                        />
                                                    </th>
                                                )}
                                                <th>Platform</th>
                                                <th>Username</th>
                                                <th>Keywords</th>
                                                <th>Description</th>
                                                <th>Status</th>
                                                <th>Created Date</th>
                                                <th style={{ width: "80px" }}>Scan</th>
                                                {userPermission && <th style={{ width: "120px" }}>Actions</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {loading ? (
                                                <tr>
                                                    <td colSpan={userPermission ? 9 : 8} className="text-center">
                                                        <div className="py-4">
                                                            <i className="fa fa-spinner fa-spin fa-2x text-muted"></i>
                                                            <p className="mt-2 text-muted">Loading blacklist entries...</p>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : client?.subscriptionType === "trial" ? (
                                                <tr>
                                                    <td colSpan={userPermission ? 9 : 8}>
                                                        <NoData />
                                                    </td>
                                                </tr>
                                            ) : blacklistData.length === 0 ? (
                                                <tr>
                                                    <td colSpan={userPermission ? 9 : 8} className="text-center">
                                                        <div className="py-4">
                                                            <i className="fa fa-list-alt fa-3x text-muted mb-3"></i>
                                                            <h5 className="text-muted">No blacklist entries found</h5>
                                                            <p className="text-muted">Start by adding your first blacklist entry</p>
                                                            {userPermission && (
                                                                <button
                                                                    onClick={() => setCreateModal(true)}
                                                                    className="btn btn-primary mt-2"
                                                                >
                                                                    Add First Entry
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ) : (
                                                blacklistData.map((entry, index) => (
                                                    <tr key={entry._id || index}>
                                                        {userPermission && (
                                                            <td>
                                                                <input
                                                                    onChange={() => singleClick(entry._id)}
                                                                    checked={selectedIds.includes(entry._id)}
                                                                    type="checkbox"
                                                                    className="checkUncheck"
                                                                />
                                                            </td>
                                                        )}
                                                        <td>
                                                            <span className="badge badge-info">
                                                                {entry.platform.charAt(0).toUpperCase() + entry.platform.slice(1)}
                                                            </span>
                                                        </td>
                                                        <td style={{ whiteSpace: "unset" }}>
                                                            <strong>{entry.userName}</strong>
                                                        </td>
                                                        <td style={{ whiteSpace: "unset", maxWidth: "300px" }}>
                                                            <div className="keywords-container">
                                                                {entry.blackListedKeywords?.length > 0 ? (
                                                                    <>
                                                                        {(expandedKeywords[entry._id] ? entry.blackListedKeywords : entry.blackListedKeywords.slice(0, 3)).map((keyword, kidx) => (
                                                                            <span key={kidx} className="badge badge-secondary mr-1 mb-1">
                                                                                {keyword}
                                                                            </span>
                                                                        ))}
                                                                        {entry.blackListedKeywords?.length > 3 && (
                                                                            <span 
                                                                                className="badge badge-light"
                                                                                style={{ cursor: "pointer" }}
                                                                                onClick={() => toggleKeywordsDisplay(entry._id)}
                                                                                title={expandedKeywords[entry._id] ? "Show less" : "Show all keywords"}
                                                                            >
                                                                                {expandedKeywords[entry._id] 
                                                                                    ? "Show less" 
                                                                                    : `+${entry.blackListedKeywords.length - 3} more`
                                                                                }
                                                                            </span>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <span className="text-muted">No keywords</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{ whiteSpace: "unset", maxWidth: "200px" }}>
                                                            {entry.description ? (
                                                                <span title={entry.description}>
                                                                    {entry.description.length > 50
                                                                        ? entry.description.substring(0, 50) + "..."
                                                                        : entry.description
                                                                    }
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">No description</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${entry.isActive ? 'badge-success' : 'badge-warning'}`}>
                                                                {entry.isActive ? 'Active' : 'Inactive'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {moment(entry.createdAt).format("DD-MM-YYYY")}
                                                        </td>
                                                        <td>
                                                            <button
                                                                className={`btn btn-sm ${scanningIds.has(entry._id) ? 'btn-warning' : 'btn-info'}`}
                                                                onClick={() => triggerManualScan(entry)}
                                                                disabled={scanningIds.has(entry._id)}
                                                                title={scanningIds.has(entry._id) ? `Scanning ${entry.userName} on ${entry.platform}...` : `Scan ${entry.userName} on ${entry.platform}`}
                                                                style={{ 
                                                                    minWidth: '40px',
                                                                    opacity: scanningIds.has(entry._id) ? 0.8 : 1
                                                                }}
                                                            >
                                                                {scanningIds.has(entry._id) ? (
                                                                    <>
                                                                        <i className="fa fa-spinner fa-spin mr-1"></i>
                                                                        <span className="small">Scanning...</span>
                                                                    </>
                                                                ) : (
                                                                    <i className="fa fa-search"></i>
                                                                )}
                                                            </button>
                                                        </td>
                                                        {userPermission && (
                                                            <td>
                                                                <div className="btn-group">
                                                                    <Link to="#"
                                                                        onClick={() => handleEdit(entry)}
                                                                        title="Edit"
                                                                    >
                                                                        Edit
                                                                    </Link>
                                                                    <Link to="#"
                                                                    className="ml-1"
                                                                        onClick={() => handleDeleteConfirm(entry._id)}
                                                                        title="Delete"
                                                                    >
                                                                        Delete
                                                                    </Link>
                                                                </div>
                                                            </td>
                                                        )}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="d-flex justify-content-between align-content-center mt-3">
                                    <div className="dataTables_paginate">
                                        {pagination.totalCount > 0 && (
                                            <Pagination
                                                className="pagination-bar"
                                                currentPage={page}
                                                totalCount={pagination.totalCount}
                                                pageSize={pageSize}
                                                onPageChange={(page) => setPage(page)}
                                            />
                                        )}
                                    </div>
                                    {pagination.totalCount > 10 && (
                                        <select
                                            value={pageSize}
                                            onChange={(e) => {
                                                setPageSize(parseInt(e.target.value));
                                                setPage(1);
                                            }}
                                            className="form-control limitSelectBox"
                                        >
                                            <option value={10}>10 per page</option>
                                            <option value={25}>25 per page</option>
                                            <option value={50}>50 per page</option>
                                            <option value={100}>100 per page</option>
                                        </select>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create Modal */}
                <div className={`modal fade Form-Wizard-modal ${isCreateModal ? "d-block show" : ""}`}>
                    <div className="modal-dialog modal-lg" id="clientModal">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Add Blacklist Entry</h5>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCreateModal(false);
                                        resetForm();
                                    }}
                                >
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <label>Platform *</label>
                                        <select
                                            className="form-control"
                                            value={formData.platform}
                                            onChange={(e) => handleInputChange('platform', e.target.value)}
                                        >
                                            <option value="">Select Platform</option>
                                            {platforms.map(p => (
                                                <option key={p.value} value={p.value}>{p.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label>Username *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.userName}
                                            onChange={(e) => handleInputChange('userName', e.target.value)}
                                            placeholder="Enter username to monitor"
                                        />
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <label>Blacklisted Keywords *</label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={keywordInput}
                                                onChange={(e) => setKeywordInput(e.target.value)}
                                                onKeyPress={handleKeywordInputKeyPress}
                                                placeholder="Enter keyword and press Enter or click Add"
                                            />
                                            <div className="input-group-append">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    type="button"
                                                    onClick={addKeyword}
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                        <div className="keywords-display mt-2">
                                            {formData.blackListedKeywords.map((keyword, index) => (
                                                <span key={index} className="badge badge-primary mr-2 mb-2">
                                                    {keyword}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm ml-1"
                                                        onClick={() => removeKeyword(keyword)}
                                                        style={{ padding: "0", border: "none", background: "none", color: "white" }}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <label>Description</label>
                                        <textarea
                                            className="form-control"
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                            rows="3"
                                            placeholder="Enter description for this blacklist entry"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setCreateModal(false);
                                        resetForm();
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={createBlacklistEntry}
                                >
                                    Create Entry
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Edit Modal */}
                <div className={`modal fade Form-Wizard-modal ${isEditModal ? "d-block show" : ""}`}>
                    <div className="modal-dialog modal-lg" id="clientModal">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Blacklist Entry</h5>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setEditModal(false);
                                        resetForm();
                                    }}
                                >
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-6">
                                        <label>Platform *</label>
                                        <select
                                            className="form-control"
                                            value={formData.platform}
                                            onChange={(e) => handleInputChange('platform', e.target.value)}
                                        >
                                            <option value="">Select Platform</option>
                                            {platforms.map(p => (
                                                <option key={p.value} value={p.value}>{p.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="col-md-6">
                                        <label>Username *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formData.userName}
                                            onChange={(e) => handleInputChange('userName', e.target.value)}
                                            placeholder="Enter username to monitor"
                                        />
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <label>Blacklisted Keywords *</label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={keywordInput}
                                                onChange={(e) => setKeywordInput(e.target.value)}
                                                onKeyPress={handleKeywordInputKeyPress}
                                                placeholder="Enter keyword and press Enter or click Add"
                                            />
                                            <div className="input-group-append">
                                                <button
                                                    className="btn btn-outline-primary"
                                                    type="button"
                                                    onClick={addKeyword}
                                                >
                                                    Add
                                                </button>
                                            </div>
                                        </div>
                                        <div className="keywords-display mt-2">
                                            {formData.blackListedKeywords.map((keyword, index) => (
                                                <span key={index} className="badge badge-primary mr-2 mb-2">
                                                    {keyword}
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm ml-1"
                                                        onClick={() => removeKeyword(keyword)}
                                                        style={{ padding: "0", border: "none", background: "none", color: "white" }}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                                <div className="row mt-3">
                                    <div className="col-md-12">
                                        <label>Description</label>
                                        <textarea
                                            className="form-control"
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                            rows="3"
                                            placeholder="Enter description for this blacklist entry"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setEditModal(false);
                                        resetForm();
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={updateBlacklistEntry}
                                >
                                    Update Entry
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Delete Confirmation Modal */}
                <div className={`modal fade Form-Wizard-modal ${isDeleteModal ? "d-block show" : ""}`}>
                    <div className="modal-dialog" id="deleteModal">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button
                                    type="button"
                                    onClick={() => setDeleteModal(false)}
                                >
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="text-center">
                                    <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                                    <h5>Are you sure?</h5>
                                    <p>Are you sure you want to delete this blacklist entry? This action cannot be undone.</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={deleteBlacklistEntry}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bulk Delete Confirmation Modal */}
                <div className={`modal fade Form-Wizard-modal ${isBulkDeleteModal ? "d-block show" : ""}`}>
                    <div className="modal-dialog" id="bulkDeleteModal">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Bulk Delete</h5>
                                <button
                                    type="button"
                                    onClick={() => setBulkDeleteModal(false)}
                                >
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div className="text-center">
                                    <i className="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
                                    <h5>Are you sure?</h5>
                                    <p>Are you sure you want to delete {selectedIds.length} selected blacklist entries? This action cannot be undone.</p>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setBulkDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={bulkDeleteBlacklistEntries}
                                >
                                    Delete All Selected
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bulk Update Keywords Modal */}
                <div className={`modal fade Form-Wizard-modal ${isBulkUpdateModal ? "d-block show" : ""}`}>
                    <div className="modal-dialog" id="bulkUpdateModal">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Update Keywords for Selected Entries</h5>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setBulkUpdateModal(false);
                                        setBulkKeywordInput("");
                                    }}
                                >
                                    <span aria-hidden="true">×</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <div>
                                    <p>You are about to update keywords for <strong>{selectedIds.length}</strong> selected entries.</p>
                                    <div className="form-group">
                                        <label>Enter Keywords (comma separated)</label>
                                        <textarea
                                            className="form-control"
                                            value={bulkKeywordInput}
                                            onChange={(e) => setBulkKeywordInput(e.target.value)}
                                            rows="4"
                                            placeholder="Example: spam, scam, fake, fraud"
                                        />
                                        <small className="form-text text-muted">
                                            Enter keywords separated by commas. These keywords will be added to all selected entries.
                                        </small>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        setBulkUpdateModal(false);
                                        setBulkKeywordInput("");
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={bulkUpdateKeywords}
                                >
                                    Update Keywords
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BlackList;
